// Shared by the bulk Assign/Remove Owner dialogs: the submit/retry/close flow,
// progress while a (possibly ~30s) bulk call runs, and the error when it fails
// outright.
import React from "react";
import { Alert, Box, LinearProgress } from "@mui/material";
import logger from "services/logger";
import { getApolloErrorMessage } from "utils/apollo";
import {
  ElasticQuery,
  removeInterestOwnersBulk_removeInterestOwnersBulk as RemoveBulkResult,
  removeInterestOwnersBulk_removeInterestOwnersBulk_failed as RemoveBulkFailure,
} from "types/gql-types";
import { OwnerOption } from "components/common/owners-editor/owner-select";

// The BulkOwnerResult/Failure app-domain types are the codegen mutation-result
// shapes with the apollo `__typename` dropped: the dialogs and button-action.tsx
// build these objects from bare literals (incl. an assign flow that has no GraphQL
// response to copy a __typename from), so the local shape stays __typename-free.
export type BulkOwnerFailure = Omit<RemoveBulkFailure, "__typename">;

export type BulkOwnerResult = Omit<RemoveBulkResult, "__typename" | "failed"> & {
  failed: BulkOwnerFailure[];
};

// No GraphQL error means the request never got an answer (Heroku cuts requests
// at 30s, or the connection dropped) while the server may still finish the
// change, so the user is sent to check rather than told it failed.
export const UNCONFIRMED_MESSAGE =
  "We couldn't confirm the update finished. Some applications may already be updated, so refresh the page to check before trying again.";

export const bulkOwnerErrorMessage = (err: unknown): string =>
  getApolloErrorMessage(err, UNCONFIRMED_MESSAGE);

type FeedbackProps = { id: string; message: string };

export const BulkOwnerProgress: React.FC<FeedbackProps> = ({ id, message }) => (
  <Box id={id} data-cy={id} role="status" sx={{ mt: 2 }}>
    <LinearProgress aria-label={message} />
    <Box component="p" sx={{ mt: 1, mb: 0, color: "text.secondary" }}>
      {`${message} This can take up to 30 seconds for a large selection.`}
    </Box>
  </Box>
);

export const BulkOwnerError: React.FC<FeedbackProps> = ({ id, message }) => (
  <Alert id={id} data-cy={id} severity="error" sx={{ mt: 2 }}>
    {message}
  </Alert>
);

// Drops a second submit that lands before the mutation's `loading` state has
// re-rendered the button disabled (e.g. a fast double click).
const useSubmitGuard = () => {
  const inFlight = React.useRef(false);
  return React.useCallback((task: () => Promise<void>): Promise<void> => {
    if (inFlight.current) return Promise.resolve();
    inFlight.current = true;
    return task().finally(() => {
      inFlight.current = false;
    });
  }, []);
};

export type BulkOwnerScope = {
  interestIds: string[] | null;
  elasticQuery: ElasticQuery | null;
  pageFrom: number | null;
  pageTo: number | null;
};

// Both bulk owner mutations take exactly this variable shape.
export type BulkOwnerVariables = BulkOwnerScope & { interestListIds: string[]; userIds: string[] };

type BulkOwnerActionOptions = {

  /** Runs the mutation and returns its result (null when the server sent none). */
  mutate: (variables: BulkOwnerVariables) => Promise<BulkOwnerResult | null | undefined>;
  isSubmitting: boolean;
  interestListIds: string[];
  scope: BulkOwnerScope;

  /** Every answered call's result, before the dialog closes or shows the summary. */
  onResult?: (result: BulkOwnerResult) => void;
  onRequestClose: () => void;
  onCancel: () => void;
};

// A partial failure keeps the owners it was sent for, so "Retry failed" re-sends
// exactly those owners.
type FailedRun = { result: BulkOwnerResult; userIds: string[] };

/**
 * The chosen owners plus submit / "Retry failed" / close for a bulk owner dialog.
 * An empty userIds is never sent: the server reads an omitted list on remove as
 * remove-every-owner.
 */
export const useBulkOwnerAction = ({
  mutate,
  isSubmitting,
  interestListIds,
  scope,
  onResult,
  onRequestClose,
  onCancel,
}: BulkOwnerActionOptions) => {
  const [selectedOwners, setSelectedOwners] = React.useState<OwnerOption[]>([]);
  // Holds a partial-failure result so the summary step renders instead of
  // closing, ensuring no failure is silently skipped.
  const [failure, setFailure] = React.useState<FailedRun | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const guardSubmit = useSubmitGuard();

  const reset = () => {
    setSelectedOwners([]);
    setFailure(null);
    setErrorMessage(null);
  };

  // A running bulk call can't be cancelled, so the dialog stays up until it answers.
  const close = () => {
    if (isSubmitting) return;
    reset();
    onRequestClose();
  };

  const cancel = () => {
    reset();
    onCancel();
  };

  const run = (runScope: BulkOwnerScope, userIds: string[]) => guardSubmit(() => {
    if (userIds.length === 0) return Promise.resolve();
    setErrorMessage(null);
    return mutate({ interestListIds, ...runScope, userIds })
      .then((answer) => {
        const result = answer ?? { succeeded: [], failed: [] };
        onResult?.(result);
        if (result.failed.length > 0) {
          setFailure({ result, userIds });
        } else {
          reset();
          onRequestClose();
        }
      })
      .catch((err) => {
        logger.sendError(err);
        setErrorMessage(bulkOwnerErrorMessage(err));
      });
  });

  const submit = () => run(scope, selectedOwners.map((option) => option.userId));

  // Retries only the failed interests (query/page nulled) with the same owners.
  const retry = () => {
    if (!failure) return;
    run(
      {
        interestIds: failure.result.failed.map((entry) => entry.interestId),
        elasticQuery: null,
        pageFrom: null,
        pageTo: null,
      },
      failure.userIds,
    );
  };

  return {
    selectedOwners,
    setSelectedOwners,
    failureResult: failure?.result ?? null,
    errorMessage,
    submit,
    retry,
    close,
    cancel,
  };
};
